import useTranslation from 'next-translate/useTranslation';
import Head from 'next/head';
import NextImage from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { Box, Flex, Image } from 'rebass/styled-components';
import styled from 'styled-components';

import Button from '@/components/Button/Button';
import Card from '@/components/Card';
import Divider from '@/components/Divider';
import { LayoutBody, LayoutWrapper } from '@/components/Layout/components/LayoutComponents';
import SpecialFooter from '@/components/Layout/components/LayoutFooter/SpecialFooter';
import ChecklistItem from '@/components/SideBySide/ChecklistItem';
import Text from '@/components/Text';
import useAuth from '@/hooks/useAuth';
import { contentApi } from '@/services';
import getHostname from '@/utils/getHostname';
import Confirmation from '@/widgets/Auth/Confirmation';
import Consultation from '@/widgets/Auth/Consultation';
import ConsultationQuery from '@/widgets/Auth/ConsultationQuery';
import Login from '@/widgets/Auth/Login';
import Target from '@/widgets/Auth/Target';
import Seo from '@/widgets/SEO/Seo';

const HelpContactSection = styled(Box)`
  margin-left: auto;
  margin-right: auto;
`;

HelpContactSection.defaultProps = {
  width: ['100%', '100%', '660px'],
};

const LogoWrapper = styled(Box)`
  width: 100%;
`;

const MyChecklistItem = styled(ChecklistItem)`
  min-width: 33% !important;
  margin-bottom: 1rem !important;
  @media (max-width: 1576px) {
    min-width: 50% !important;
  }
  @media (max-width: 1200px) {
    min-width: 100% !important;
    margin-bottom: 0.1rem !important;
  }
`;
LogoWrapper.defaultProps = {
  alignItems: 'center',
  display: 'flex',
  marginBottom: '1.563rem',
};

const MyFlexLeft = styled(Flex)`
  width: 34%;
  @media (max-width: 1576px) {
    width: 50%;
  }
  @media (max-width: 1000px) {
    width: 100%;
    max-height: 100%;
  }
`;

const MyFlex = styled(Flex)`
  @media (min-width: 1001px) {
    min-height: 100vh;
  }
  @media (max-width: 1000px) {
    min-height: 0;
  }
`;

const MyFlexRight = styled(Flex)`
  width: 66%;
  @media (max-width: 1576px) {
    width: 50%;
  }
  @media (max-width: 1000px) {
    display: none;
  }
`;

const MyCardBlur = styled(Card)`
  margin-bottom: 3.75rem;
  @media (max-height: 820px) and (max-width: 1200px) {
    margin-bottom: 1rem;
  }
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  max-height: max-content;
  width: 100%;
  background: linear-gradient(0deg, rgba(75, 65, 64, 0.9), rgba(75, 65, 64, 0.8)), rgba(75, 65, 64, 0.6);
  @supports ((-webkit-backdrop-filter: none) or (backdrop-filter: none)) {
    background: linear-gradient(0deg, rgba(0, 0, 0, 0.0463257), rgba(0, 0, 0, 0.0463257)), rgba(250, 250, 250, 0.0001);
    backdrop-filter: blur(135.914px);
  }
  padding: 1.25rem 2.5rem 1.25em;
`;

export default function Auth() {
  const router = useRouter();
  const { t } = useTranslation('content-key-cloak-content');
  const checkList = t('checkList', {}, { returnObjects: true });
  const { t: h } = useTranslation('common-header');
  const logo = h('logo', {}, { returnObjects: true });
  const { authenticated, login: klogin, initialized } = useAuth();

  const [page, setPage] = useState('');
  const setCurrentPage = (newpage) => {
    return () => {
      setPage(newpage);
    };
  };
  useEffect(() => {
    if (!authenticated && initialized && router.query.page === 'Target') {
      klogin?.();
    }

    if (router.query.page === 'Target' || router.query.page === 'Confirmation' || router.query.page === 'Login') {
      setPage(router.query.page);
    }

    if (router.query.page === 'ConsultationQuery') {
      setPage(router.query.page);
      router.replace(
        {
          pathname: '/auth',
        },
        undefined,
        { shallow: true }
      );
    }
  }, [authenticated, initialized, klogin, router, page, router.query.page]);

  return (
    <LayoutWrapper>
      <Seo page="page-reconciliation" />
      <Head>
        <title>Registrierung</title>
        <link rel="shortcut icon" href={t('favIconLarge.url')} />
      </Head>
      <LayoutBody>
        <Flex flexDirection="column" flex={1}>
          <Flex flexDirection={['column', 'column', 'row']} height="100%">
            <MyFlexLeft flexDirection="column" width={['100%', '100%', '34%']}>
              <MyFlex
                flex="1"
                flexDirection="column"
                px={['1.255rem', '1.25rem', '3.75rem']}
                py={['1.255rem', '1.255rem', '3.75rem']}
              >
                <LogoWrapper>
                  <Link href="/">
                    <a title="Zur Startseite">
                      <Image alt={logo.alternativeText} height="28px" src={logo.url} style={{ cursor: 'pointer' }} />
                    </a>
                  </Link>
                </LogoWrapper>
                {page === 'ConsultationQuery' ? <ConsultationQuery setCurrentPage={setCurrentPage} /> : null}
                {page === 'Consultation' ? <Consultation setCurrentPage={setCurrentPage} /> : null}
                {page === 'Confirmation' ? <Confirmation /> : null}
                {page === 'Target' ? <Target setCurrentPage={setCurrentPage} /> : null}
                {page === 'Login' ? <Login setCurrentPage={setCurrentPage} /> : null}
              </MyFlex>
              <Box display={['block', 'none', 'none']} px={['1.255rem', '1.25rem', '3.75rem']}>
                <Divider />
              </Box>
              {page !== 'Confirmation' && page !== 'Target' ? (
                <>
                  <Flex
                    flexDirection={['row', 'column', 'column']}
                    backgroundColor={['white', 'white', 'backgroundGrey']}
                    px={['1.255rem', '1.255rem', '3.75rem']}
                    py={['1.255rem', '1.255rem', '3.75rem']}
                  >
                    <Box width="100%">
                      <Text
                        mb={['1.438rem', '1.438rem', '1.438rem']}
                        variant="body"
                        fontSize={['14px', '14px', '14px']}
                      >
                        Sie besitzen bereits einen Zugang?
                      </Text>
                      <Button
                        mb={[0, 0, 0]}
                        onClick={() => klogin({ redirectUri: `${document.location.origin}/dashboard` })}
                        variant="secondary"
                        width={['100%', '100%', '100%']}
                      >
                        Anmelden
                      </Button>
                    </Box>
                  </Flex>
                  <Flex
                    flexDirection={['row', 'column', 'column']}
                    backgroundColor={['white', 'white', 'backgroundGrey']}
                    px={['1.255rem', '1.255rem', '3.75rem']}
                    py={['1.255rem', '1.255rem', '2rem']}
                  >
                    <SpecialFooter />
                  </Flex>
                </>
              ) : (
                <Flex
                  flexDirection={['row', 'column', 'column']}
                  backgroundColor={['white', 'white', 'backgroundGrey']}
                  px={['1.255rem', '1.255rem', '3.75rem']}
                  py={['1.255rem', '1.255rem', '2rem']}
                >
                  <SpecialFooter />
                </Flex>
              )}
            </MyFlexLeft>
            <MyFlexRight
              display={['none', 'none', 'flex']}
              height="100%"
              maxHeight="100vh"
              overflow="hidden"
              width={['100%', '100%', '66%']}
              sx={{ position: 'fixed', top: '0', right: '0' }}
            >
              <Box
                display={['none', 'flex', 'flex']}
                height={['100%', '100%', '100%']}
                width={['100%', '100%', '100%']}
                sx={{ right: '0', zIndex: 0 }}
              >
                <NextImage
                  alt={t('bgImage.alternativeText')}
                  layout="fill"
                  priority
                  objectFit="cover"
                  objectPosition="right top"
                  quality={90}
                  src={t('bgImage.formats.large.url')}
                />
              </Box>
              <Flex
                flexDirection={['row', 'column', 'column']}
                sx={{ position: 'absolute', paddingLeft: '3.75rem', top: '9.7rem', zIndex: 1 }}
                width={['100%', '100%', '100%']}
              >
                <Text color="white" variant="title">
                  {t('headline')}
                </Text>
              </Flex>
              <Flex
                flexDirection={['row', 'column', 'column']}
                sx={{
                  bottom: '2.5rem',
                  paddingLeft: '3.75rem',
                  paddingRight: '0.331rem',
                  position: 'absolute',
                  zIndex: 1,
                }}
                width={['100%', '100%', '100%']}
              >
                <MyCardBlur>
                  <Flex flexDirection={['row', 'column', 'row']} flexWrap="wrap">
                    {checkList.map((item) => (
                      <MyChecklistItem auth key={item.id}>
                        {item.text}
                      </MyChecklistItem>
                    ))}
                  </Flex>
                </MyCardBlur>
                <Flex
                  alignItems="center"
                  flexDirection={['row', 'column', 'row']}
                  justifyContent="flex-end"
                  mr={['4.188rem', '4.188rem', '4.188rem']}
                >
                  <Text color="white" mr={['1.25rem', '1.25rem', '1.25rem']} variant="bodyLightSmallNativ">
                    {t('ServiceText')}
                  </Text>
                  <Image src={t('logo.url')} alt={t('logo.alternativeText')} height="71px" />
                </Flex>
              </Flex>
            </MyFlexRight>
          </Flex>
        </Flex>
      </LayoutBody>
    </LayoutWrapper>
  );
}

export async function getServerSideProps({ locale, req }) {
  return {
    props: {
      content: await contentApi('/reconciliation', locale, getHostname(req)),
      hideLayout: true,
    },
  };
}
